import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import RocketlaneAPI from '../services/rocketlaneApi';

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dateFilter, setDateFilter] = useState('last30days');
  const [customDateRange, setCustomDateRange] = useState({ startDate: null, endDate: null });
  const [currentEnvironment, setCurrentEnvironment] = useState('PROD'); // Default to PROD
  const [apiKey, setApiKey] = useState(null);
  const [showApiKeyPrompt, setShowApiKeyPrompt] = useState(false);

  // Create API instance
  const [rocketlaneAPI] = useState(() => new RocketlaneAPI());

  // Initialize environment and API key from localStorage
  useEffect(() => {
    const savedEnvironment = localStorage.getItem('rocketlane-current-environment');
    if (savedEnvironment && ['PROD', 'SANDBOX'].includes(savedEnvironment)) {
      setCurrentEnvironment(savedEnvironment);
    }
    
    // Set the hardcoded API key
    const hardcodedApiKey = 'rl-2dcbce94-b80c-4154-8f96-1289e8dc85cf';
    setApiKey(hardcodedApiKey);
    rocketlaneAPI.setApiKey(hardcodedApiKey);
    localStorage.setItem('rocketlane-api-key', hardcodedApiKey);
  }, [rocketlaneAPI]);

  // Set the API environment
  const setApiEnvironment = useCallback((environment) => {
    rocketlaneAPI.setEnvironment(environment);
  }, [rocketlaneAPI]);

  // Handle API key submission
  const handleApiKeySubmit = useCallback(async (newApiKey) => {
    console.log('=== HANDLING API KEY SUBMISSION ===');
    
    try {
      // Set the API key
      rocketlaneAPI.setApiKey(newApiKey);
      
      // Test the connection
      const testResult = await rocketlaneAPI.testConnection();
      if (!testResult.success) {
        throw new Error(testResult.message || 'Invalid API key');
      }
      
      // Save the API key
      localStorage.setItem('rocketlane-api-key', newApiKey);
      setApiKey(newApiKey);
      setShowApiKeyPrompt(false);
      setLoading(true);
      
      // We'll trigger the data fetch through the useEffect that watches apiKey changes
      
    } catch (error) {
      console.error('API key submission failed:', error);
      throw error;
    }
  }, [rocketlaneAPI]);

  // Get date range based on filter
  const getDateRange = useCallback((filter = dateFilter) => {
    const now = new Date();
    let startDate, endDate;

    switch (filter) {
      case 'last7days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'last30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'last90days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'custom':
        if (customDateRange.startDate && customDateRange.endDate) {
          startDate = new Date(customDateRange.startDate);
          endDate = new Date(customDateRange.endDate);
        } else {
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          endDate = now;
        }
        break;
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
    }

    return {
      startISO: startDate.toISOString(),
      endISO: endDate.toISOString()
    };
  }, [dateFilter, customDateRange]);

  // Test API connection
  const testApiConnection = useCallback(async () => {
    console.log('=== TESTING API CONNECTION ===');
    try {
      setApiEnvironment(currentEnvironment);
      
      // Test basic connectivity
      const testResult = await rocketlaneAPI.testConnection();
      console.log('API test result:', testResult);
      
      if (testResult.success) {
        console.log('✅ API connection successful');
        
        // Test individual endpoints
        console.log('Testing projects endpoint...');
        const projects = await rocketlaneAPI.getProjects();
        console.log(`Projects found: ${projects.length}`);
        
        console.log('Testing users endpoint...');
        const users = await rocketlaneAPI.getUsers();
        console.log(`Users found: ${users.length}`);
        
        return true;
      } else {
        console.error('❌ API connection failed:', testResult.message);
        return false;
      }
    } catch (error) {
      console.error('❌ API test error:', error);
      return false;
    }
  }, [currentEnvironment, setApiEnvironment, rocketlaneAPI]);

  // Memoized fetch function using new API methods
  const fetchDashboardData = useCallback(async (forceRefresh = false, dateRange = null) => {
    console.log(`=== STARTING DASHBOARD DATA FETCH ===`);
    console.log(`Environment: ${currentEnvironment}`);
    console.log(`Force refresh: ${forceRefresh}`);
    console.log(`Date range:`, dateRange);
    
    setLoading(true);
    setError(null);
    
    try {
      // Set the API environment
      console.log('Setting API environment...');
      setApiEnvironment(currentEnvironment);
      console.log('API environment set successfully');
      
      // Test API connection first
      console.log('Testing API connection...');
      const apiWorking = await testApiConnection();
      if (!apiWorking) {
        throw new Error('API connection test failed. Please check your API key and network connection.');
      }
      
      // Clear cache to ensure fresh data
      if (forceRefresh) {
        console.log('Clearing API cache...');
        rocketlaneAPI.clearCache();
      }
      
      // Use the new getDashboardData method that handles all pagination correctly
      const effectiveDateRange = dateRange || getDateRange();
      console.log('Using date range:', effectiveDateRange);
      
      const data = await rocketlaneAPI.getDashboardData(effectiveDateRange);
      
      console.log(`${currentEnvironment} dashboard data fetched successfully:`, data);
      console.log('=== SETTING DASHBOARD DATA ===');
      setDashboardData(data);
      setLastUpdated(new Date());
      console.log('=== DASHBOARD DATA SET SUCCESSFULLY ===');
    } catch (err) {
      console.error(`=== ERROR FETCHING DASHBOARD DATA ===`);
      console.error(`Error fetching ${currentEnvironment} dashboard data:`, err);
      setError(err.message || `Failed to fetch ${currentEnvironment} dashboard data.`);
      setDashboardData(null);
    } finally {
      console.log('=== SETTING LOADING TO FALSE ===');
      setLoading(false);
      console.log('=== DASHBOARD DATA FETCH COMPLETE ===');
    }
  }, [currentEnvironment, setApiEnvironment, rocketlaneAPI, getDateRange, testApiConnection]);

  // Memoized refresh function
  const refreshData = useCallback(async (dateRange = null) => {
    setIsRefreshing(true);
    try {
      await fetchDashboardData(true, dateRange);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchDashboardData]);

  // Memoized environment change handler
  const handleEnvironmentChange = useCallback((newEnvironment) => {
    console.log(`Switching environment from ${currentEnvironment} to ${newEnvironment}`);
    setCurrentEnvironment(newEnvironment);
    localStorage.setItem('rocketlane-current-environment', newEnvironment);
    setDashboardData(null);
    setError(null);
    fetchDashboardData(true);
  }, [currentEnvironment, fetchDashboardData]);



  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    dashboardData,
    loading,
    error,
    lastUpdated,
    isRefreshing,
    refreshData,
    handleEnvironmentChange,
    currentEnvironment,
    dateFilter,
    setDateFilter,
    customDateRange,
    setCustomDateRange,
    testApiConnection,
    apiKey,
    showApiKeyPrompt,
    handleApiKeySubmit,
    rocketlaneAPI
  }), [dashboardData, loading, error, lastUpdated, isRefreshing, refreshData, handleEnvironmentChange, currentEnvironment, dateFilter, customDateRange, testApiConnection, apiKey, showApiKeyPrompt, handleApiKeySubmit, rocketlaneAPI]);

  useEffect(() => {
    console.log('=== DASHBOARD CONTEXT INITIALIZED ===');
    console.log('Current environment:', currentEnvironment);
    console.log('RocketlaneAPI instance:', rocketlaneAPI);
    console.log('API key available:', !!apiKey);
    console.log('Show API key prompt:', showApiKeyPrompt);
    
    // Only fetch data if we have an API key and don't need to show the prompt
    if (apiKey && !showApiKeyPrompt) {
      console.log('Starting initial data fetch...');
      fetchDashboardData();
    }
  }, [fetchDashboardData, apiKey, showApiKeyPrompt, currentEnvironment, rocketlaneAPI]);

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};
